import React, { useState } from 'react'
import { useFormContext, type RegisterOptions } from 'react-hook-form'

import { Button } from '@/legacy-src/components/ui/button'
import { cn } from '@/legacy-src/lib/utils'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import Icon from '../icons/Icon'
import ErrorMessage from './ErrorMessage'
import { useIsFieldInvalid } from './hooks/useIsFieldInvalid'

interface InputFieldProps {
  label?: React.ReactNode
  name: string
  type?: string
  required?: boolean
  placeholder?: string
  prefixText?: string
  disabled?: boolean
  registerOptions?: RegisterOptions
  content?: React.ReactNode
  optional?: boolean
  showEye?: boolean
  className?: string
  labelClassName?: string
  inputClassName?: string
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  required,
  registerOptions,
  placeholder,
  prefixText,
  content,
  type = 'text',
  optional,
  showEye = false,
  className,
  labelClassName,
  inputClassName,
  ...props
}) => {
  const { t } = getTranslate()
  const isInvalid = useIsFieldInvalid(name)
  const { register } = useFormContext()

  const [showText, setShowText] = useState(false)

  return (
    <>
      <div className={`form-group ${className}`}>
        {label && (
          <label htmlFor={name} className={cn('relative text-sm', labelClassName)}>
            {label}
            {required && <sup className="static text-base">*</sup>}
            {optional && (
              <span className="text-lowercase font-normal text-text-color"> ({t(`registration.optional`)})</span>
            )}
          </label>
        )}
        {content}
        <div className="relative">
          <input
            id={name}
            {...props}
            type={showText ? '' : type}
            {...register(name, registerOptions)}
            placeholder={placeholder}
            className={cn(`form-control placeholder-sand-700`, inputClassName, isInvalid ? 'is-invalid' : '')}
            autoComplete="off"
          />
          {showEye && (
            <Button
              onClick={() => setShowText(!showText)}
              variant="inline"
              className="absolute inset-y-0 right-4 m-0 p-0 focus:shadow-none"
            >
              <Icon className="" color="primary" size="sm" icon={showText ? 'eye' : 'eye-disabled'} />
            </Button>
          )}
        </div>
        <ErrorMessage name={name} />
      </div>
    </>
  )
}

export default InputField
